import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquareQuote, Plus, Pencil, Trash2, ImagePlus, X, Save } from "lucide-react";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

const EMPTY = { couple_name: "", quote: "", photo: "", active: true, order: 0 };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [displayCount, setDisplayCount] = useState(3);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(Array.isArray(data) ? data : []);
      const { data: s } = await api.get("/admin/testimonial-settings");
      setDisplayCount(s.display_count || 3);
    } catch {
      toast.error("Gagal memuat testimonial");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDlgOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      couple_name: it.couple_name || "",
      quote: it.quote || "",
      photo: it.photo || "",
      active: it.active !== false,
      order: it.order || 0,
    });
    setDlgOpen(true);
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await resizeImage(file, 800);
      setForm((f) => ({ ...f, photo: b64 }));
    } catch { toast.error("Gagal mengunggah foto"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.couple_name.trim() || !form.quote.trim()) {
      toast.error("Nama pasangan dan kutipan wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) {
        await api.put(`/admin/testimonials/${editing.id}`, payload);
        toast.success("Testimonial diperbarui");
      } else {
        await api.post("/admin/testimonials", payload);
        toast.success("Testimonial ditambahkan");
      }
      setDlgOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/testimonials/${it.id}`, {
        couple_name: it.couple_name, quote: it.quote, photo: it.photo || "",
        active: !it.active, order: it.order || 0,
      });
      await load();
    } catch { toast.error("Gagal mengubah status"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Hapus testimonial "${it.couple_name}"?`)) return;
    try {
      await api.delete(`/admin/testimonials/${it.id}`);
      toast.success("Testimonial dihapus");
      await load();
    } catch { toast.error("Gagal menghapus"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/testimonial-settings", { display_count: Number(displayCount) || 1 });
      toast.success("Pengaturan tersimpan");
    } catch { toast.error("Gagal menyimpan pengaturan"); }
    finally { setSavingSettings(false); }
  };

  return (
    <div>
      <PageTitle
        title="Manajemen Testimonial"
        desc="Kelola testimonial pasangan yang tampil di Landing Page."
        icon={MessageSquareQuote}
      />

      {/* Settings */}
      <Card className="mb-6 p-5" data-testid="testimonial-settings-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xs">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Jumlah tampil di Landing</Label>
            <Input
              data-testid="testimonial-display-count"
              type="number" min="1" max="50"
              value={displayCount}
              onChange={(e) => setDisplayCount(e.target.value)}
              className="mt-1.5 w-32"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Menampilkan hingga {displayCount} testimonial aktif teratas (urut berdasarkan Order).
            </p>
          </div>
          <Button data-testid="save-testimonial-settings" onClick={saveSettings} disabled={savingSettings} className="gap-1.5">
            <Save className="h-4 w-4" /> {savingSettings ? "Menyimpan..." : "Simpan Pengaturan"}
          </Button>
        </div>
      </Card>

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Total {items.length} testimonial · {items.filter((i) => i.active).length} aktif
        </p>
        <Button data-testid="add-testimonial-btn" onClick={openNew} className="gap-1.5">
          <Plus className="h-4 w-4" /> Tambah Testimonial
        </Button>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Memuat…</p>
      ) : items.length === 0 ? (
        <EmptyState text="Belum ada testimonial. Klik 'Tambah Testimonial' untuk memulai." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <Card key={it.id} data-testid={`testimonial-row-${it.id}`} className="flex flex-col p-4">
              <div className="flex items-start gap-3">
                {it.photo ? (
                  <img src={it.photo} alt={it.couple_name}
                    className="h-16 w-16 rounded-full object-cover ring-2 ring-primary/40" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <MessageSquareQuote className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold">{it.couple_name}</h3>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant={it.active ? "default" : "outline"} className="text-[10px]">
                      {it.active ? "Aktif" : "Nonaktif"}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground">Order: {it.order || 0}</span>
                  </div>
                </div>
                <Switch
                  data-testid={`toggle-testimonial-${it.id}`}
                  checked={!!it.active}
                  onCheckedChange={() => toggleActive(it)}
                />
              </div>
              <p className="mt-3 line-clamp-4 flex-1 text-sm italic text-muted-foreground">"{it.quote}"</p>
              <div className="mt-4 flex justify-end gap-2">
                <Button data-testid={`edit-testimonial-${it.id}`} onClick={() => openEdit(it)} size="sm" variant="outline" className="gap-1">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button data-testid={`delete-testimonial-${it.id}`} onClick={() => remove(it)} size="sm" variant="outline" className="gap-1 text-red-600 hover:text-red-700">
                  <Trash2 className="h-3.5 w-3.5" /> Hapus
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit Testimonial" : "Tambah Testimonial"}
            </DialogTitle>
            <DialogDescription className="sr-only">Formulir testimonial pasangan</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex flex-col items-center gap-3">
              {form.photo ? (
                <div className="relative">
                  <img src={form.photo} alt="Foto Pasangan" className="h-28 w-28 rounded-full object-cover ring-2 ring-primary/50" />
                  <button
                    type="button"
                    data-testid="testimonial-remove-photo"
                    onClick={() => setForm((f) => ({ ...f, photo: "" }))}
                    className="absolute -right-1 -top-1 rounded-full bg-black/70 p-1 text-white"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex h-28 w-28 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <ImagePlus className="h-8 w-8" />
                </div>
              )}
              <label className="cursor-pointer text-xs font-medium text-primary underline underline-offset-4" data-testid="testimonial-upload-btn">
                {uploading ? "Memuat..." : (form.photo ? "Ganti foto" : "Upload foto pasangan")}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; onUpload(f); e.target.value = ""; }} />
              </label>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nama Pasangan *</Label>
              <Input
                data-testid="testimonial-couple-name"
                className="mt-1.5"
                value={form.couple_name}
                onChange={(e) => setForm((f) => ({ ...f, couple_name: e.target.value }))}
                placeholder="mis. Rani & Andi"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kutipan / Testimoni *</Label>
              <Textarea
                data-testid="testimonial-quote"
                className="mt-1.5 min-h-[100px]"
                value={form.quote}
                onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                placeholder="Wedifylab membantu kami merapikan seluruh persiapan pernikahan..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Order</Label>
                <Input
                  data-testid="testimonial-order"
                  type="number" className="mt-1.5"
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                />
                <p className="mt-1 text-[10px] text-muted-foreground">Kecil = tampil lebih dulu</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Aktif</Label>
                <div className="mt-3 flex items-center gap-2">
                  <Switch
                    data-testid="testimonial-active"
                    checked={!!form.active}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                  />
                  <span className="text-sm">{form.active ? "Ditampilkan" : "Disembunyikan"}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} data-testid="testimonial-cancel">Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="testimonial-save">
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
